import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import SidePanel from '../components/SidePanel';
import ViewFormModal from '../components/ViewFormModal';

const AdminPage = () => {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [error, setError] = useState(null);
  const [nameFilter, setNameFilter] = useState('');
  const [telephoneFilter, setTelephoneFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_BASE_URL_API}/api/contacts`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setContacts(data);
          setFilteredContacts(data);
        } else {
          const data = await response.json();
          setError(data.message || 'Failed to fetch contacts');
          if (response.status === 401) {
            navigate('/login');
          }
        }
      } catch (error) {
        setError('Error fetching contacts');
      }
    };

    fetchContacts();
  }, [navigate]);

  useEffect(() => {
    let filtered = contacts;
    if (nameFilter) {
      filtered = filtered.filter(contact =>
        contact.name.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }
    if (telephoneFilter) {
      filtered = filtered.filter(contact =>
        contact.telephone.includes(telephoneFilter)
      );
    }
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(contact => new Date(contact.createdAt) >= start);
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(contact => new Date(contact.createdAt) <= end);
    }
    setFilteredContacts(filtered);
  }, [nameFilter, telephoneFilter, startDate, endDate, contacts]);

  

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL_API}/api/contacts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setContacts(contacts.filter(contact => contact.id !== id));
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to delete contact');
      }
    } catch (error) {
      setError('Error deleting contact');
    }
  };

  const handleClearAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL_API}/api/contacts`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setContacts([]);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to clear contacts');
      }
    } catch (error) {
      setError('Error clearing contacts');
    }
  };

  return (
    <div className="flex h-screen bg-[#f4f7fa]">
      <SidePanel handleLogout={handleLogout} />
      <div className="flex-1 p-10">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-[#364A9A] font-poppins">Forms</h1>
        </div>
        {error && <p className="text-red-500 text-center mb-4">{error}</p>}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-[#364A9A] font-poppins">Submitted Forms</h2>
            <button
              onClick={handleClearAll}
              className="bg-red-500 text-white font-bold py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transition duration-300 ease-in-out transform hover:scale-105 font-poppins"
            >
              Clear All Forms
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <input
              type="text"
              placeholder="Filter by name"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#364A9A]"
            />
            <input
              type="text"
              placeholder="Filter by telephone"
              value={telephoneFilter}
              onChange={(e) => setTelephoneFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#364A9A]"
            />
            <input
              type="date"
              placeholder="Start date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#364A9A]"
            />
            <input
              type="date"
              placeholder="End date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#364A9A]"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b">Name</th>
                  <th className="py-2 px-4 border-b">Email</th>
                  <th className="py-2 px-4 border-b">Telephone</th>
                  <th className="py-2 px-4 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact) => (
                  <tr key={contact.id}>
                    <td className="py-2 px-4 border-b">{contact.name}</td>
                    <td className="py-2 px-4 border-b">{contact.email}</td>
                    <td className="py-2 px-4 border-b">{contact.telephone}</td>
                    <td className="py-2 px-4 border-b">
                      <button
                        onClick={() => setSelectedContact(contact)}
                        className="bg-blue-500 text-white font-bold py-1 px-2 rounded-lg mr-2"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(contact.id)}
                        className="bg-red-500 text-white font-bold py-1 px-2 rounded-lg"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <ViewFormModal contact={selectedContact} onClose={() => setSelectedContact(null)} />
      </div>
    </div>
  );
};

export default AdminPage;
